/**
 * The width of one docked right-hand panel, as React state: read from the
 * per-user memory on mount, live while the edge is dragged, remembered when
 * the drag ends. Pair it with `PanelResizeHandle`.
 */
import { useCallback, useEffect, useState } from "react";

import {
  boundsForWindow,
  clampPanelWidth,
  forgetPanelWidth,
  rememberPanelWidth,
  storedPanelWidth,
  type PanelWidthBounds,
} from "./panel-width.js";

export interface DockedPanelWidth {
  width: number;
  bounds: PanelWidthBounds;
  /** Live, while dragging or nudging — not yet remembered. */
  setWidth(width: number): void;
  /** The drag ended: remember what it is now. */
  commit(): void;
  /** Back to the panel's own default, and forget the memory. */
  reset(): void;
}

function windowBounds(): PanelWidthBounds {
  return boundsForWindow(typeof window === "undefined" ? 1600 : window.innerWidth);
}

export function useDockedPanelWidth(key: string, defaultWidth: number): DockedPanelWidth {
  const [bounds, setBounds] = useState<PanelWidthBounds>(windowBounds);
  // The width the user CHOSE (or the default), kept apart from what is shown:
  // a window that shrinks clamps the display, and one that grows back gets the
  // chosen width back — a clamp must never quietly become the new choice.
  const [chosen, setChosen] = useState(() => storedPanelWidth(key, defaultWidth));
  const width = clampPanelWidth(chosen, bounds);

  useEffect(() => {
    const onResize = () => setBounds(windowBounds());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const setWidth = useCallback(
    (next: number) => setChosen(clampPanelWidth(next, bounds)),
    [bounds],
  );
  const commit = useCallback(() => {
    setChosen((current) => {
      rememberPanelWidth(key, current);
      return current;
    });
  }, [key]);
  const reset = useCallback(() => {
    forgetPanelWidth(key);
    setChosen(clampPanelWidth(defaultWidth));
  }, [key, defaultWidth]);

  return { width, bounds, setWidth, commit, reset };
}
